import { useNode } from "@craftjs/core";
import React, { useEffect, useState } from "react";
import ReactPlayer from "react-player";
import styled from "styled-components";
import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import {
  VIDEO_POSTER_PRELOAD,
  withVideoPosterFrame,
} from "../../../../utils/videoPosterFrame";
import TextView from "../../../TextView";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import { getVideoCarryForwardPatch } from "./carryForwardProps";
import MediaFallback from "../../Shared/MediaFallback";
import MediaSkeleton from "../../../MediaSkeleton";
import VideoSettings from "./VideoSettings";
import { useSelector, useDispatch } from "react-redux";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { isBlobUrl } from "../../../../utils/segmentPayloadHygiene";

const VideoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  height: 180px; // Set a fixed height or adjust as needed
`;

const TextContainer = styled.div`
  background-color: #344054;
  padding: 8px;
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
`;

// Convert Loom share URLs to embed URLs
export const getVideoUrl = (url) => {
  if (!url) return url;

  // Check if it's a Loom share URL and convert to embed URL
  if (url.includes("loom.com/share/")) {
    return url.replace("/share/", "/embed/");
  }

  return url;
};

export const Video = ({
  src,
  alt,
  width,
  isApplyMarginOnAllSides,
  allSideMargin,
  margin,
  borderRadius,
  borderColor,
  borderWidth,
  showVideoTitle,
  title,
  hyperlink,
  showHyperlink,
  dropShadowColor,
  isLoading,
}) => {
  const [showControls, setShowControls] = useState(false);
  // Defensive: a dead/blocked/invalid video URL shows a clean placeholder
  // instead of failing silently. Additive — only set on a real player error.
  const [hasError, setHasError] = useState(false);

  // BUG-editwheel-0bcd60: a persisted `blob:` src is a tab-local object-URL
  // (URL.createObjectURL) — it can never resolve outside the browser
  // session that created it. The save-time guard (segmentPayloadHygiene.js
  // stripBlobMediaSrcs, wired into generateWheelPayload) already blanks a
  // blob src to "" on every save going forward, but a wheel saved BEFORE
  // that guard existed can still carry a dead blob: src in its persisted
  // node. Feeding that straight to ReactPlayer fires onError and shows the
  // scary "Video unavailable" fallback for every viewer, forever, even
  // though the fix is just "re-upload." Treat a blob src exactly like no
  // src at all — the normal empty/"add a video" placeholder — instead of an
  // error state. Read-only: this never mutates the stored prop (safe on the
  // read-only viewer Frame, not just the editor canvas) — the very next
  // real save still blanks it via the existing write-side guard.
  const hasUsableSrc = Boolean(src) && !isBlobUrl(src);

  const videoUrl = getVideoUrl(hasUsableSrc ? src : null);

  // A new source is a fresh attempt — clear any previous error.
  useEffect(() => {
    setHasError(false);
  }, [src]);

  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();
  const { video: videoProps } = useSelector((state) => state.codeblockStyles);
  const dispatch = useDispatch();

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const border = borderColor
    ? `${borderWidth}px solid ${getPaletteColor(borderColor)}`
    : "none";

  // Initialize with default props from reducer for new components.
  // WEB-034: hyperlink/showHyperlink are deliberately excluded from what
  // carries forward — see carryForwardProps.js for the field list + why.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        Object.assign(props, getVideoCarryForwardPatch(props, videoProps));
      });
    }, 5);

    return () => clearTimeout(timeoutId);
  }, []);

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Video);

  // Check if the video is Loom and decide which component to render
  const isLoomVideo = videoUrl ? videoUrl?.includes("loom.com/embed/") : false;

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        {isLoading ? (
          <MediaSkeleton minHeight={180} label="Loading video" />
        ) : hasUsableSrc && hasError ? (
          <MediaFallback
            variant="video"
            style={{ borderRadius, border }}
          />
        ) : hasUsableSrc ? (
          <VideoContainer
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
            style={{
              padding: marginStyle,
            }}
          >
            <div
              style={{
                borderRadius,
                overflow: "hidden",
                border,
                cursor: showHyperlink ? "pointer" : "default",
                boxShadow:
                  dropShadowColor &&
                  `0px 0px 9.3px 0px ${getPaletteColor(dropShadowColor)}`,
                width: width || "100%", // Make sure it takes the full width of the container
                background: "transparent",
                height: "100%", // Ensure iframe fills the container
              }}
            >
              {/* Render Loom video iframe */}
              {isLoomVideo ? (
                <iframe
                  src={videoUrl}
                  title={title || "Embedded video"}
                  frameBorder="0"
                  allowFullScreen
                  style={{
                    width: "100%",
                    height: "100%", // Make the iframe fill the container
                    borderRadius,
                  }}
                ></iframe>
              ) : (
                <ReactPlayer
                  /* The `#t=0.1` media fragment makes the browser paint a real
                     first frame as this block's still image instead of leaving
                     a black box. Applied through the SHARED helper, which is a
                     no-op for provider URLs (YouTube/Vimeo) whose poster is the
                     provider's business, so this stays safe for every source
                     type the block accepts. */
                  url={withVideoPosterFrame(videoUrl)}
                  controls={true}
                  width="100%"
                  height="100%"
                  playing={false}
                  onError={() => setHasError(true)}
                  config={{
                    youtube: {
                      playerVars: {
                        controls: 1,
                        showinfo: 1,
                        modestbranding: 1,
                        rel: 0,
                        disablekb: 0,
                        fs: 1,
                        playsinline: 1,
                      },
                    },
                    file: {
                      attributes: {
                        frameBorder: "0",
                        allowFullScreen: true,
                        /* MEASURED LIVE 2026-08-05 on main.378ce494.js: this
                           element rendered `preload="auto"` (ReactPlayer's
                           default), i.e. the browser downloaded the WHOLE video
                           just to show a thumbnail — while the capture card on
                           /capture, reading the SAME asset in the same session,
                           correctly read "metadata". This is the one line that
                           closes that gap. */
                        preload: VIDEO_POSTER_PRELOAD,
                      },
                    },
                  }}
                />
              )}
              {showVideoTitle && !!title && (
                <TextContainer style={{ width: "100%" }}>
                  <TextView
                    variant="largeText"
                    style={{ color: colors.white, fontWeight: 500 }}
                  >
                    {title}
                  </TextView>
                </TextContainer>
              )}
            </div>
          </VideoContainer>
        ) : (
          <Image src={ASSETS.segmentEditor.UploadVideo} />
        )}
      </BlockIndicator>
    </div>
  );
};

export const VideoDefaultProps = {
  src: null,
  alt: "New video",
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  width: undefined,
  borderRadius: undefined,
  borderWidth: undefined,
  title: "Untitled Video",
  showVideoTitle: undefined,
  hyperlink: undefined,
  showHyperlink: undefined,
  dropShadowColor: undefined,
  borderColor: undefined,
  isLoading: false,
};

Video.craft = {
  props: VideoDefaultProps,
  related: {
    settings: VideoSettings,
  },
};
